import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import placeEnhancementsService, { PlaceReminder } from "@/services/PlaceEnhancementsService"
import { parsedUser } from "@/common/GetCurrentUser"
import API from "@/common/API"

interface PlaceRemindersTabProps {
  placeId: string
  onRemindersChange?: () => void
}

const PlaceRemindersTab: React.FC<PlaceRemindersTabProps> = ({ placeId, onRemindersChange }) => {
  const { t } = useTranslation("global")
  const currentUser = parsedUser()
  const [reminders, setReminders] = useState<PlaceReminder[]>(() => placeEnhancementsService.getReminders(placeId))
  const [showModal, setShowModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "completed">("all")
  const [orgUsers, setOrgUsers] = useState<any[]>([])

  // Form state
  const [title, setTitle] = useState("")
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().substring(0, 10))
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium")
  const [assignedUserName, setAssignedUserName] = useState("")
  const [notes, setNotes] = useState("")

  const loadOrgUsers = async () => {
    try {
      if (currentUser?.OrganizationId) {
        const res = await API.getAction("users/list", "OrganizationId=" + currentUser.OrganizationId)
        if (res?.data?.status === "ok" && Array.isArray(res.data.response)) {
          setOrgUsers(res.data.response)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    loadOrgUsers()
  }, [])

  const reloadReminders = () => {
    const list = placeEnhancementsService.getReminders(placeId)
    setReminders(list)
    if (onRemindersChange) onRemindersChange()
  }

  const handleOpenAdd = () => {
    setTitle("")
    setDueDate(new Date().toISOString().substring(0, 10))
    setPriority("medium")
    setAssignedUserName(orgUsers.length > 0 ? `${orgUsers[0].firstName || ""} ${orgUsers[0].lastName || ""}`.trim() : "")
    setNotes("")
    setShowModal(true)
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    placeEnhancementsService.addReminder({
      placeId,
      title,
      dueDate,
      priority,
      status: "pending",
      assignedUserName,
      notes,
    })

    setShowModal(false)
    reloadReminders()
  }

  const handleToggleStatus = (id: string) => {
    placeEnhancementsService.toggleReminderStatus(id)
    reloadReminders()
  }

  const handleDelete = (id: string) => {
    if (window.confirm(t("¿Está seguro de eliminar este recordatorio?"))) {
      placeEnhancementsService.deleteReminder(id)
      reloadReminders()
    }
  }

  const filteredReminders = reminders.filter(r => {
    if (filterStatus === "pending") return r.status === "pending"
    if (filterStatus === "completed") return r.status === "completed"
    return true
  })

  const getPriorityBadge = (p: PlaceReminder["priority"]) => {
    if (p === "high") return <Badge bg="danger">{t("Alta Prioridad")}</Badge>
    if (p === "medium") return <Badge bg="warning" text="dark">{t("Prioridad Media")}</Badge>
    return <Badge bg="info">{t("Baja Prioridad")}</Badge>
  }

  return (
    <div className="py-2">
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <Form.Select
          size="sm"
          className="rounded-3"
          style={{ width: 160 }}
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as any)}
        >
          <option value="all">{t("Todas las tareas")}</option>
          <option value="pending">{t("Solo pendientes")}</option>
          <option value="completed">{t("Solo completadas")}</option>
        </Form.Select>
        <Button variant="warning" size="sm" className="text-dark fw-semibold rounded-3" onClick={handleOpenAdd}>
          <i className="fas fa-plus me-1" />
          {t("Nuevo Recordatorio")}
        </Button>
      </div>

      {filteredReminders.length === 0 ? (
        <Alert variant="light" className="border text-center py-4 rounded-3">
          <i className="fas fa-check-double fa-2x mb-2 text-muted" />
          <div className="text-muted small">{t("No se encontraron recordatorios registrados.")}</div>
          <Button variant="outline-warning" size="sm" className="mt-2 text-dark rounded-3" onClick={handleOpenAdd}>
            <i className="fas fa-plus me-1" />
            {t("Crear Primer Recordatorio")}
          </Button>
        </Alert>
      ) : (
        <Table hover responsive className="align-middle small mb-0 border rounded-3 overflow-hidden">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}></th>
              <th>{t("Tarea / Título")}</th>
              <th>{t("Fecha Límite")}</th>
              <th>{t("Prioridad")}</th>
              <th>{t("Asignado a")}</th>
              <th className="text-end">{t("Acciones")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredReminders.map(r => {
              const isDone = r.status === "completed"
              return (
                <tr key={r.id} className={isDone ? "table-light text-muted" : ""}>
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={isDone}
                      onChange={() => handleToggleStatus(r.id)}
                    />
                  </td>
                  <td>
                    <div className={`fw-semibold ${isDone ? "text-decoration-line-through" : ""}`}>{r.title}</div>
                    {r.notes && <small className="text-muted d-block">{r.notes}</small>}
                  </td>
                  <td>
                    <i className="fas fa-calendar-day me-1 text-muted" />
                    {r.dueDate}
                  </td>
                  <td>{getPriorityBadge(r.priority)}</td>
                  <td>
                    <Badge bg="light" text="dark" className="border">
                      <i className="fas fa-user me-1 text-primary" />
                      {r.assignedUserName || "Sin asignar"}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button variant="outline-danger" size="sm" className="px-2 py-0 border-0 bg-transparent" onClick={() => handleDelete(r.id)}>
                      <i className="fas fa-trash" />
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {/* Modal Nuevo Recordatorio */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton className="border-bottom">
            <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
              <i className="fas fa-bell text-warning" />
              {t("Nuevo Recordatorio de la Propiedad")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="small">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Título / Tarea a Realizar")} *</Form.Label>
                <Form.Control
                  size="sm"
                  required
                  className="rounded-3"
                  placeholder={t("ej. Dar seguimiento a aprobación de cotización")}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Fecha Límite")} *</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  required
                  className="rounded-3"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Prioridad")}</Form.Label>
                <Form.Select
                  size="sm"
                  className="rounded-3"
                  value={priority}
                  onChange={e => setPriority(e.target.value as any)}
                >
                  <option value="low">{t("Baja Prioridad")}</option>
                  <option value="medium">{t("Prioridad Media")}</option>
                  <option value="high">{t("Alta Prioridad")}</option>
                </Form.Select>
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Asignar a Persona de la Organización")}</Form.Label>
                <Form.Select
                  size="sm"
                  className="rounded-3"
                  value={assignedUserName}
                  onChange={e => setAssignedUserName(e.target.value)}
                >
                  <option value="">{t("Sin asignar (General)")}</option>
                  {orgUsers.map(u => {
                    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || u.userName
                    return (
                      <option key={u.userId || u.id} value={fullName}>
                        {fullName} ({u.email || u.roleName || "Usuario"})
                      </option>
                    )
                  })}
                </Form.Select>
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Notas y Detalles")}</Form.Label>
                <Form.Control
                  size="sm"
                  as="textarea"
                  rows={2}
                  className="rounded-3"
                  placeholder={t("Escriba contexto o detalles adicionales para esta tarea...")}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="warning" size="sm" className="text-dark fw-bold w-100 py-2 rounded-3" type="submit">
              {t("Guardar Recordatorio")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default PlaceRemindersTab
